import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckIcon } from "@/components/icons";

export const BillingCard = () => {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cycle, setCycle] = useState("monthly"); // monthly | annual
  const pollRef = useRef(null);

  const fetchBilling = useCallback(
    () => api.getBilling().then(setBilling).catch(() => {}),
    []
  );

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  // Auto-refresh when returning to this tab, or when another tab (the Checkout
  // success page) signals a billing change — so Pro status appears without a reload.
  useEffect(() => {
    const onFocus = () => fetchBilling();
    const onVisible = () => document.visibilityState === "visible" && fetchBilling();
    const onStorage = (e) => e.key === "firstrun_billing_refresh" && fetchBilling();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
      clearInterval(pollRef.current);
    };
  }, [fetchBilling]);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const b = await api.getBilling();
        setBilling(b);
        if (b.plan === "pro") {
          clearInterval(pollRef.current);
          toast.success("You're on Pro — enjoy unlimited chats! 🎉");
        }
      } catch (_) {}
      if (tries >= 45) {
        clearInterval(pollRef.current);
        setBilling((b) => {
          if (b && b.plan !== "pro") toast.info("Finished checkout? Refresh the page to update your plan.");
          return b;
        });
      }
    }, 4000);
  }, []);

  const upgrade = async () => {
    setLoading(true);
    // Open a tab synchronously (within the click gesture) so it isn't blocked as a popup,
    // and so Stripe Checkout renders top-level — never inside a nested preview iframe.
    const checkoutTab = window.open("", "_blank");
    try {
      const lookupKey = cycle === "annual" ? "pro_annual" : "pro_monthly";
      const { checkout_url } = await api.createCheckout(window.location.origin, lookupKey);
      if (checkoutTab) {
        checkoutTab.location.href = checkout_url;
      } else {
        // Popup blocked — fall back to top-level navigation (breaks out of any iframe).
        (window.top || window).location.href = checkout_url;
      }
      // Poll so the dashboard flips to Pro automatically once payment completes.
      startPolling();
    } catch (_) {
      if (checkoutTab) checkoutTab.close();
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const manageSubscription = async () => {
    setPortalLoading(true);
    // Open synchronously so it's not popup-blocked and renders top-level (portal can't be framed).
    const portalTab = window.open("", "_blank");
    try {
      const { portal_url } = await api.billingPortal(window.location.origin + "/dashboard");
      if (portal_url && portalTab) portalTab.location.href = portal_url;
      else if (portal_url) (window.top || window).location.href = portal_url;
    } catch (e) {
      if (portalTab) portalTab.close();
      toast.error(e?.response?.data?.detail || "Could not open the billing portal.");
    } finally {
      setPortalLoading(false);
    }
  };

  if (!billing) {
    return (
      <div className="paper border border-border rounded-2xl p-6 flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading plan…
      </div>
    );
  }

  const isPro = billing.plan === "pro";
  const { used, limit, unlimited } = billing.usage;
  const pct = unlimited ? 8 : Math.min(100, Math.round((used / limit) * 100));
  const left = Math.max(0, limit - used);

  return (
    <div data-testid="billing-card" className="flex flex-col gap-6">
      <div className="paper rounded-2xl border border-border p-6 flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <span className="text-base font-semibold">Messages this month</span>
          <span
            data-testid="plan-badge"
            className="font-mono text-xs px-2.5 py-1 rounded-xl border border-input"
          >
            {isPro ? "Pro" : "Free"}
          </span>
        </div>
        <div data-testid="usage-count" className="font-heading text-[56px] leading-none tracking-[-0.02em]">
          {used}
          <span className="font-sans text-lg tracking-normal text-muted-foreground">
            {unlimited ? " · unlimited" : ` of ${limit}`}
          </span>
        </div>
        <div className="h-2.5 rounded-md bg-track overflow-hidden">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-sm text-soft">
          {unlimited ? "Unlimited on Pro." : `${left} message${left === 1 ? "" : "s"} left. Resets on the 1st.`}
        </div>
      </div>

      {isPro ? (
        <div className="paper rounded-2xl border border-border p-6 flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Pro plan</span>
            <span className="text-[13px] px-2.5 py-1 rounded-xl bg-success/10 text-success capitalize">
              {billing.status || "active"}
            </span>
          </div>
          <p className="text-[15px] leading-normal text-soft">
            You are on Pro. Enjoy unlimited messages across the workspace and your embedded widget.
          </p>
          <button
            data-testid="manage-subscription-button"
            onClick={manageSubscription}
            disabled={portalLoading}
            className="h-[46px] rounded-xl border border-foreground text-[15px] font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {portalLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            Manage subscription
          </button>
        </div>
      ) : (
        <div className="paper-dark rounded-2xl text-primary-foreground p-6 flex flex-col gap-4">
          <div className="text-lg font-semibold">Never hit a limit</div>
          <ul className="text-[15px] leading-normal text-[#D8D4C8] flex flex-col gap-2">
            {["Unlimited messages", "Priority answer quality", "Higher widget traffic"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <CheckIcon size={16} className="text-accent" /> {t}
              </li>
            ))}
          </ul>

          <div
            className="flex gap-1 p-1 rounded-xl border border-[#3A3F44] relative z-10"
            data-testid="billing-cycle-toggle"
          >
            {[
              ["monthly", "Monthly", "cycle-monthly"],
              ["annual", "Annual · 2 months free", "cycle-annual"],
            ].map(([id, label, tid]) => (
              <button
                key={id}
                data-testid={tid}
                onClick={() => setCycle(id)}
                className={`flex-1 h-10 rounded-[10px] text-sm font-medium transition-colors ${
                  cycle === id ? "bg-accent text-accent-foreground" : "text-[#D8D4C8] hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div data-testid="cycle-price">
            {cycle === "annual" ? (
              <>
                <div className="font-heading text-[40px] leading-none">
                  $290<span className="font-sans text-[15px] text-[#D8D4C8]"> /year</span>
                </div>
                <div className="text-sm text-accent mt-1.5">About $24.17 a month. Save $58 a year.</div>
              </>
            ) : (
              <div className="font-heading text-[40px] leading-none">
                $29<span className="font-sans text-[15px] text-[#D8D4C8]"> /month</span>
              </div>
            )}
          </div>

          <button
            data-testid="upgrade-button"
            onClick={upgrade}
            disabled={loading}
            className="relative z-10 h-[46px] rounded-xl bg-accent text-accent-foreground text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {cycle === "annual" ? "Upgrade to Pro, $290/yr" : "Upgrade to Pro, $29/mo"}
          </button>
        </div>
      )}
    </div>
  );
};
